import { Check, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui-kit/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { cn } from '@/lib/utils';

/**
 * OrgCreationProgressDialog
 *
 * A multi-step, animated progress dialog that reflects the real status of the
 * organization-creation pipeline (logo upload → IAM → signature → UDS →
 * membership). Each step animates between pending → active (spinner) → done
 * (check) or error states, with a connecting rail that fills as steps complete.
 *
 * The dialog is non-dismissible while work is in flight; once it finishes or
 * errors, the close affordance (and an optional retry) become available.
 */

export type StepStatus = 'pending' | 'active' | 'done' | 'error';

export interface ProgressStep {
  key: string;
  label: string;
  description?: string;
  status: StepStatus;
}

interface OrgCreationProgressDialogProps {
  open: boolean;
  steps: ProgressStep[];
  error?: string | null;
  onClose?: () => void;
  onRetry?: () => void;
  /** Override the running-state header copy (defaults to org-creation wording)
   *  so other flows (e.g. the embed bootstrap/analyze) can reuse this dialog. */
  loadingTitle?: string;
  loadingSubtitle?: string;
}

const StepIcon = ({ status, index }: { status: StepStatus; index: number }) => {
  const base =
    'flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-xs font-semibold transition-all duration-300';

  if (status === 'done') {
    return (
      <span className={cn(base, 'border-primary bg-primary text-primary-foreground')}>
        <Check className="h-4 w-4 animate-in zoom-in-50 duration-300" />
      </span>
    );
  }

  if (status === 'active') {
    return (
      <span className={cn(base, 'border-primary text-primary')}>
        <Loader2 className="h-4 w-4 animate-spin" />
      </span>
    );
  }

  if (status === 'error') {
    return (
      <span className={cn(base, 'border-destructive bg-destructive text-destructive-foreground')}>
        <AlertCircle className="h-4 w-4 animate-in zoom-in-50 duration-300" />
      </span>
    );
  }

  return <span className={cn(base, 'border-border text-muted-foreground')}>{index + 1}</span>;
};

export const OrgCreationProgressDialog = ({
  open,
  steps,
  error,
  onClose,
  onRetry,
  loadingTitle,
  loadingSubtitle,
}: OrgCreationProgressDialogProps) => {
  const hasError = steps.some((s) => s.status === 'error') || !!error;
  const isComplete = steps.length > 0 && steps.every((s) => s.status === 'done');
  const isRunning = !hasError && !isComplete;

  const headerTitle = hasError
    ? 'Something went wrong'
    : isComplete
      ? 'Organization created'
      : (loadingTitle ?? 'Setting up your organization');

  const headerSubtitle = hasError
    ? error || 'We hit a snag while creating your organization.'
    : isComplete
      ? "You're all set — taking you to your new organization."
      : (loadingSubtitle ?? 'Hang tight, this only takes a moment.');

  return (
    <Dialog open={open} onOpenChange={(next) => !next && !isRunning && onClose?.()}>
      <DialogContent
        hideClose={isRunning}
        className="max-w-md"
        onEscapeKeyDown={(e) => isRunning && e.preventDefault()}
      >
        <DialogHeader>
          <div className="flex items-start gap-3">
            <span
              className={cn(
                'flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition-colors',
                hasError ? 'bg-destructive/10 text-destructive' : 'bg-primary/10 text-primary'
              )}
            >
              {hasError ? (
                <AlertCircle className="h-5 w-5" />
              ) : isComplete ? (
                <Check className="h-5 w-5 animate-in zoom-in-50 duration-300" />
              ) : (
                <Loader2 className="h-5 w-5 animate-spin" />
              )}
            </span>
            <div className="min-w-0">
              <DialogTitle className="text-base leading-tight">{headerTitle}</DialogTitle>
              <DialogDescription className="mt-0.5">{headerSubtitle}</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <ol className="mt-2">
          {steps.map((step, index) => {
            const isLast = index === steps.length - 1;
            const railFilled = step.status === 'done';
            return (
              <li key={step.key} className="flex gap-3">
                <div className="flex flex-col items-center">
                  <StepIcon status={step.status} index={index} />
                  {!isLast && (
                    <span
                      className={cn(
                        'my-1 w-px flex-1 transition-colors duration-500',
                        railFilled ? 'bg-primary' : 'bg-border'
                      )}
                      style={{ minHeight: '1.25rem' }}
                    />
                  )}
                </div>
                <div className={cn('pb-4', isLast && 'pb-0')}>
                  <p
                    className={cn(
                      'text-sm font-medium transition-colors duration-300',
                      step.status === 'pending' && 'text-muted-foreground',
                      step.status === 'active' && 'text-foreground',
                      step.status === 'done' && 'text-foreground',
                      step.status === 'error' && 'text-destructive'
                    )}
                  >
                    {step.label}
                  </p>
                  {step.description && (
                    <p className="mt-0.5 text-xs text-muted-foreground">{step.description}</p>
                  )}
                </div>
              </li>
            );
          })}
        </ol>

        {hasError && (
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onClose?.()}>
              Close
            </Button>
            {onRetry && <Button onClick={() => onRetry()}>Try again</Button>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
